// The chat harness's memory of the analytics API: lessons learned from
// failed queries and the fixes that followed, kept per tenant and user
// in the browser, decayed when not reconfirmed, and retired when the
// schema they were learned against changes underneath them.
//
// An entry never holds predicate values or result rows. Its scope is an
// entity name, field and edge enums, and an error class; its example is
// a pair of spec SHAPES (see harnessMemoryCapture.js); its evidence is
// chat and tool-call ids. That is what makes it safe to persist and to
// print into a prompt.
//
// Confidence has two parts. The stored `confidence` moves on explicit
// events: a confirmation pulls it toward 1, a contradiction scales it
// down. The EFFECTIVE confidence, which decides ranking, prompt
// inclusion, and archival, also decays with time since the last
// confirmation, so a lesson nobody has needed in a month fades out on
// its own and a backend fix that makes a lesson obsolete does not leave
// a stale rule in every future prompt.

import { browserStorageKey } from "./browserStorageScope.js";
import { describeDiff, matchEntries, schemaHash, schemaNames } from "./harnessMemoryCapture.js";

export const STORAGE_PREFIX = "origin-harness-memory-v1";
export const DECAY_PER_WEEK = 0.9;
export const ARCHIVE_BELOW = 0.2;
export const MAX_ACTIVE = 40;
export const MAX_ARCHIVED = 40;
export const MAX_EVIDENCE = 5;
export const PROMPT_LIMIT = 12;
export const MAX_RULE_CHARS = 400;

const WEEK_MS = 7 * 24 * 3600 * 1000;

export const INITIAL_CONFIDENCE = Object.freeze({
    lesson: 0.7,
    pitfall: 0.4,
    user: 0.9,
    agent: 0.75,
});

function newId() {
    const bytes = new Uint8Array(4);
    if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return "m_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function cleanRule(rule) {
    return String(rule || "").replace(/\s+/g, " ").trim().slice(0, MAX_RULE_CHARS);
}

function cleanScope(scope = {}, knownNames = null) {
    const entity = typeof scope.entity === "string" && scope.entity ? scope.entity : null;
    let fields = Array.isArray(scope.fields) ? scope.fields.filter((f) => typeof f === "string") : [];
    if (knownNames) fields = fields.filter((f) => knownNames.fields.has(f));
    return {
        tool: scope.tool || "analytics_query",
        entity: knownNames && entity && !knownNames.entities.has(entity) ? null : entity,
        fields: fields.slice(0, 12),
        errorClass: typeof scope.errorClass === "string" && scope.errorClass ? scope.errorClass : null,
    };
}

export function effectiveConfidence(entry, now = Date.now()) {
    const base = Number(entry?.confidence) || 0;
    const since = Date.parse(entry?.lastConfirmedAt || entry?.createdAt || 0);
    if (!Number.isFinite(since)) return base;
    const weeks = Math.max(0, (now - since) / WEEK_MS);
    return base * Math.pow(DECAY_PER_WEEK, weeks);
}

// `storage` is any object with getItem/setItem/removeItem; `keyFor`
// resolves the identity-scoped storage key (null while the identity is
// unknown, in which case nothing is persisted and the store is empty).
export function createHarnessMemoryStore({
    storage = null,
    keyFor = () => browserStorageKey(STORAGE_PREFIX),
    now = () => Date.now(),
    idFor = newId,
} = {}) {
    const subscribers = new Set();
    let loadedKey = null;
    let lastRaw = null;
    let entries = [];
    let currentSchemaHash = null;
    // Names the live schema serves, kept from the last reconcile. A lesson
    // about a WRONG field name mentions that name in its rule; its scope
    // must only carry names that exist, or the next reconcile would retire
    // the lesson as stale the moment it was learned.
    let knownNames = null;

    function resolveStorage() {
        if (storage) return storage;
        try { return globalThis.localStorage || null; } catch { return null; }
    }

    // Re-read whenever the persisted text differs from what this instance
    // last saw, so two tabs (or two module instances under a dev server)
    // converge on the latest write instead of resurrecting each other's
    // forgotten entries.
    function load() {
        const key = keyFor();
        let raw = null;
        if (key) {
            try { raw = resolveStorage()?.getItem(key) ?? null; } catch { raw = null; }
        }
        if (key === loadedKey && raw === lastRaw) return;
        loadedKey = key;
        lastRaw = raw;
        entries = [];
        if (!key) return;
        try {
            const parsed = raw ? JSON.parse(raw) : null;
            if (parsed && Array.isArray(parsed.entries)) entries = parsed.entries.filter((e) => e && e.id && e.rule);
            if (parsed?.schemaHash) currentSchemaHash = parsed.schemaHash;
        } catch { entries = []; }
    }

    function persist() {
        if (!loadedKey) return;
        try {
            const serialized = JSON.stringify({ version: 1, schemaHash: currentSchemaHash, entries });
            resolveStorage()?.setItem(loadedKey, serialized);
            lastRaw = serialized;
        } catch { /* storage full or unavailable; the in-memory copy still serves this session */ }
        for (const cb of subscribers) cb();
    }

    function archive(entry, reason) {
        entry.status = "archived";
        entry.archivedAt = new Date(now()).toISOString();
        entry.archiveReason = reason;
    }

    // Decay and caps. Runs before every read that matters so a store
    // that sat idle for weeks is trimmed the moment it is next used.
    function settle() {
        const t = now();
        for (const e of entries) {
            if (e.status !== "active") continue;
            if (effectiveConfidence(e, t) < ARCHIVE_BELOW) archive(e, "decayed");
        }
        const active = entries.filter((e) => e.status === "active").sort((a, b) => effectiveConfidence(b, t) - effectiveConfidence(a, t));
        for (const e of active.slice(MAX_ACTIVE)) archive(e, "capacity");
        const archived = entries.filter((e) => e.status !== "active").sort((a, b) => Date.parse(b.archivedAt || 0) - Date.parse(a.archivedAt || 0));
        if (archived.length > MAX_ARCHIVED) {
            const drop = new Set(archived.slice(MAX_ARCHIVED).map((e) => e.id));
            entries = entries.filter((e) => !drop.has(e.id));
        }
    }

    function active() {
        load();
        settle();
        const t = now();
        return entries.filter((e) => e.status === "active").sort((a, b) => effectiveConfidence(b, t) - effectiveConfidence(a, t));
    }

    function pushEvidence(entry, evidence) {
        if (!evidence) return;
        entry.evidence = [{ ...evidence, at: new Date(now()).toISOString() }, ...(entry.evidence || [])].slice(0, MAX_EVIDENCE);
    }

    function insert({ kind, rule, scope, example, signatureKey, evidence, source, confidence }) {
        const stamp = new Date(now()).toISOString();
        const entry = {
            id: idFor(),
            kind,
            source,
            rule: cleanRule(rule),
            scope: cleanScope(scope, knownNames),
            example: example || null,
            signatureKey: signatureKey || null,
            evidence: [],
            schemaHash: currentSchemaHash,
            createdAt: stamp,
            lastConfirmedAt: stamp,
            confirmations: 0,
            contradictions: 0,
            confidence,
            status: "active",
        };
        pushEvidence(entry, evidence);
        entries.push(entry);
        return entry;
    }

    const store = {
        // Stamp the store with the live schema and retire lessons whose
        // entity or fields the schema no longer serves.
        reconcileSchema(schema) {
            load();
            const hash = schemaHash(schema);
            if (!hash) return { hash: currentSchemaHash, retired: 0 };
            const names = schemaNames(schema);
            knownNames = names;
            let retired = 0;
            for (const e of entries) {
                if (e.status !== "active") continue;
                const entityGone = e.scope?.entity && !names.entities.has(e.scope.entity);
                const fieldGone = (e.scope?.fields || []).some((f) => !names.fields.has(f));
                if (entityGone || fieldGone) { archive(e, "schema_changed"); retired++; }
            }
            const changed = hash !== currentSchemaHash;
            currentSchemaHash = hash;
            if (changed || retired) persist();
            return { hash, retired };
        },

        schemaHash() { load(); return currentSchemaHash; },

        active,

        list({ includeArchived = false } = {}) {
            load();
            settle();
            return includeArchived ? entries.slice() : entries.filter((e) => e.status === "active");
        },

        get(id) { load(); return entries.find((e) => e.id === id) || null; },

        effectiveConfidence(entry) { return effectiveConfidence(entry, now()); },

        // Find stored lessons that speak to a fresh failure.
        match({ signature, shape }) {
            return matchEntries(active(), { signature, shape });
        },

        // Insert a distilled lesson, or confirm the existing entry with
        // the same signature key instead of storing a duplicate.
        upsertLesson({ rule, scope, example, signatureKey, evidence, kind = "lesson", source = "auto" }) {
            load();
            const clean = cleanRule(rule);
            if (!clean) return null;
            const existing = signatureKey ? entries.find((e) => e.status === "active" && e.signatureKey === signatureKey) : null;
            if (existing) {
                if (existing.kind === "pitfall" && kind === "lesson") {
                    existing.kind = "lesson";
                    existing.rule = clean;
                    existing.example = example || existing.example;
                    existing.confidence = Math.max(existing.confidence, INITIAL_CONFIDENCE.lesson);
                }
                return store.confirm(existing.id, evidence);
            }
            const entry = insert({
                kind, rule: clean, scope, example, signatureKey, evidence, source,
                confidence: INITIAL_CONFIDENCE[kind] ?? INITIAL_CONFIDENCE.pitfall,
            });
            settle();
            persist();
            return entry;
        },

        // A lesson the user or the agent states outright.
        remember({ rule, entity = null, fields = [], source = "user", evidence = null }) {
            load();
            const clean = cleanRule(rule);
            if (!clean) return null;
            const duplicate = entries.find((e) => e.status === "active" && e.rule.toLowerCase() === clean.toLowerCase());
            if (duplicate) return store.confirm(duplicate.id, evidence);
            const entry = insert({
                kind: "lesson", rule: clean, scope: { entity, fields }, example: null, signatureKey: null, evidence, source,
                confidence: source === "user" ? INITIAL_CONFIDENCE.user : INITIAL_CONFIDENCE.agent,
            });
            settle();
            persist();
            return entry;
        },

        confirm(id, evidence = null) {
            load();
            const entry = entries.find((e) => e.id === id);
            if (!entry) return null;
            entry.confirmations = (entry.confirmations || 0) + 1;
            entry.confidence = Math.min(1, entry.confidence + (1 - entry.confidence) * 0.3);
            entry.lastConfirmedAt = new Date(now()).toISOString();
            if (entry.status !== "active" && entry.archiveReason !== "schema_changed") {
                entry.status = "active";
                delete entry.archivedAt;
                delete entry.archiveReason;
            }
            pushEvidence(entry, evidence);
            settle();
            persist();
            return entry;
        },

        contradict(id, evidence = null) {
            load();
            const entry = entries.find((e) => e.id === id);
            if (!entry) return null;
            entry.contradictions = (entry.contradictions || 0) + 1;
            entry.confidence = entry.confidence * 0.6;
            pushEvidence(entry, evidence);
            if (entry.contradictions >= 3 && entry.contradictions > (entry.confirmations || 0)) archive(entry, "contradicted");
            settle();
            persist();
            return entry;
        },

        forget(id) {
            load();
            if (id === "all") {
                const n = entries.length;
                entries = [];
                persist();
                return n;
            }
            const before = entries.length;
            entries = entries.filter((e) => e.id !== id);
            const removed = before - entries.length;
            if (removed) persist();
            return removed;
        },

        // The block the system prompt carries. Top lessons by effective
        // confidence; each line names the id so the user can /forget it.
        promptEntries({ limit = PROMPT_LIMIT } = {}) {
            return active().slice(0, limit);
        },

        promptSection({ limit = PROMPT_LIMIT } = {}) {
            const top = active().slice(0, limit);
            if (!top.length) return "";
            const t = now();
            const lines = top.map((e) => {
                const scope = e.scope?.entity ? e.scope.entity.replace("ENTITY_TYPE_", "") : "any entity";
                const conf = effectiveConfidence(e, t).toFixed(2);
                const fix = e.example?.diff?.length ? ` (what worked: ${describeDiff(e.example.diff)})` : "";
                return `- ${e.id} [${scope}, ${conf}] ${e.rule}${fix}`;
            });
            return [
                "<harness_lessons>",
                "Lessons this harness learned in earlier chats about the analytics API. Each came from a real failed query and, where a fix is shown, the change that made the retry succeed. They decay unless reconfirmed, so treat them as strong hints, not law.",
                ...lines,
                "When a query fails and you work out the fix, call `remember` with the general rule so the next chat starts from it.",
                "</harness_lessons>",
            ].join("\n");
        },

        stats() {
            load();
            settle();
            const t = now();
            const act = entries.filter((e) => e.status === "active");
            return {
                active: act.length,
                archived: entries.length - act.length,
                schemaHash: currentSchemaHash,
                stale: act.filter((e) => e.schemaHash && currentSchemaHash && e.schemaHash !== currentSchemaHash).length,
                meanConfidence: act.length ? act.reduce((s, e) => s + effectiveConfidence(e, t), 0) / act.length : 0,
            };
        },

        subscribe(cb) {
            subscribers.add(cb);
            return () => subscribers.delete(cb);
        },

        // Drop the in-memory copy so the next read re-resolves the
        // identity key. Used on logout and by tests.
        reset() {
            loadedKey = null;
            lastRaw = null;
            entries = [];
            currentSchemaHash = null;
            knownNames = null;
        },
    };
    return store;
}

export const harnessMemory = createHarnessMemoryStore();
